import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle, Quote } from 'lucide-react';
import SEO from '../components/SEO';
import LazyImage from '../components/LazyImage';

const CaseStudySpiideo = () => {
  const siteUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://orboroi.com';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Spiideo AB Sports AI - Video Annotation & Validation Case Study",
    "description": "How Orboroi annotated 30,000+ sports videos with 99%+ accuracy for Spiideo AB's AI-powered sports analytics platform.",
    "image": "https://images.unsplash.com/photo-1613758403772-268da019247a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjB2aWRlbyUyMHRyYWNraW5nJTIwcGxheWVyJTIwZGV0ZWN0aW9uJTIwb2JqZWN0JTIwdHJhY2tpbmclMjBhbmFseXRpY3N8ZW58MHx8fHwxNzc0Mjk4NTkxfDA&ixlib=rb-4.1.0&q=85",
    "datePublished": "2024-02-10",
    "dateModified": "2024-02-10",
    "author": {
      "@type": "Organization",
      "name": "Orboroi"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Orboroi",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/case-study-spiideo`
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Case Studies", url: "/case-studies" },
    { name: "Spiideo AB", url: "/case-study-spiideo" }
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO
        title="Spiideo AB Sports AI Case Study"
        description="How Orboroi annotated 30,000+ sports videos with 99%+ accuracy for Spiideo AB's AI-powered sports analytics and player tracking platform."
        canonical="/case-study-spiideo"
        type="article"
        author="Orboroi Team"
        datePublished="2024-02-10"
        structuredData={structuredData}
        breadcrumbs={breadcrumbs}
        image="https://images.unsplash.com/photo-1613758403772-268da019247a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjB2aWRlbyUyMHRyYWNraW5nJTIwcGxheWVyJTIwZGV0ZWN0aW9uJTIwb2JqZWN0JTIwdHJhY2tpbmclMjBhbmFseXRpY3N8ZW58MHx8fHwxNzc0Mjk4NTkxfDA&ixlib=rb-4.1.0&q=85"
      />
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Link to="/" className="hover:text-[#379F7F]">Home</Link>
          <span>→</span>
          <Link to="/case-studies" className="hover:text-[#379F7F]">Case Studies</Link>
          <span>→</span>
          <span className="text-white">Spiideo AB</span>
        </div>
      </div>

      <section className="py-12 bg-gradient-to-b from-blue-900 to-cyan-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Case Study — <span className="text-[#379F7F]">Spiideo AB</span>
            </h1>
            <p className="text-xl text-gray-300 mb-2">Sports AI</p>
            <p className="text-gray-400">End-to-End AI Operations</p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            
            <div>
              <h2 className="text-3xl font-bold text-white mb-4">The Challenge</h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                Spiideo needed large-scale, precise video annotation to track players, objects, and events across sports footage for automated video analysis and analytics. Their in-house capacity could not meet the volume or specialized requirements.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-white mb-4">Our Solution</h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Annotated 30,000+ sports videos, including player tracking, object tracking, and event time stamping. Embedded annotation teams into Spiideo's workflow to adapt to evolving model architecture. Maintained accuracy and consistency through rigorous QA checks and HITL oversight. Provided insights to improve model performance over the course of a year-long collaboration.
              </p>
              <div className="case-study-image-container">
                <LazyImage 
                  src="https://images.unsplash.com/photo-1613758403772-268da019247a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjB2aWRlbyUyMHRyYWNraW5nJTIwcGxheWVyJTIwZGV0ZWN0aW9uJTIwb2JqZWN0JTIwdHJhY2tpbmclMjBhbmFseXRpY3N8ZW58MHx8fHwxNzc0Mjk4NTkxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Sports video analysis showing Spiideo AB's AI-powered player tracking and analytics platform"
                  className="case-study-image"
                />
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Results</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Seamless integration into existing AI workflows</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">High-volume annotations delivered with 99%+ accuracy</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Enhanced model capabilities for sports video analysis</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Annotated 30,000+ sports videos with player and object tracking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Year-long collaboration with embedded annotation teams</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Real-time insights enabled through improved model performance</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <Quote className="w-12 h-12 text-[#379F7F] mb-4" />
              <p className="text-xl text-gray-200 italic mb-6 leading-relaxed">
                "After working together for more than a year, we have built a strong foundation of collaboration and trust. Like the gears of a well-oiled machine, the team members have integrated well in our workflow and evolve and change according to our priorities."
              </p>
              <div className="border-t border-gray-700 pt-4">
                <p className="text-white font-semibold">Daniel Milesson</p>
                <p className="text-gray-400">Tech Lead Machine Learning, Spiideo AB</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 mt-12 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                Ready to Scale Your AI with Data Certainty?
              </h3>
              <p className="text-gray-300 mb-6">
                Let's discuss how Orboroi can help accelerate your AI development.
              </p>
              <Link to="/contact">
                <Button className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold">
                  Get in Touch
                </Button>
              </Link>
            </div>

            <div className="pt-8">
              <Link to="/case-studies" className="text-[#379F7F] hover:underline inline-flex items-center">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to All Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudySpiideo;
